import React, { useState, useEffect } from 'react';
import Select from '../ui/Select';
import DateRangeSelector from '../DateRangeSelector';
import { useDN } from '../../contexts/DNContext';
import useAnalytics from '../../hooks/useAnalytics';
import { API_BASE_URL } from '../../config';
import InfoTooltip from '../../utils/Tooltip';
import { _t } from '../../utils/i18n';

const DNSelectionHeader = ({
  projects,
  selectedProject,
  setSelectedProject,
  loading,
  shopSigns,
  selectedShopSigns,
  setSelectedShopSigns,
  categories,
  selectedCategories,
  setSelectedCategories,
  currentUser,
  fetchWithAuth,
  obfuscate,
  jail = false
}) => {
  const { startDate, endDate, setStartDate, setEndDate, setPresetType, setPresetValue } = useDN();
  const { track } = useAnalytics({ currentUser, project: selectedProject?.label });
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastMeasure, setLastMeasure] = useState(null);

  useEffect(() => {
    const fetchLastMeasure = async () => {
      if (selectedProject) {
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/last-measure`);
          const { date } = await response.json();
          setLastMeasure(date);
        } catch (error) {
          console.error('Error fetching last measure:', error);
        }
      }
    };
    fetchLastMeasure();
  }, [selectedProject, fetchWithAuth]);

  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate, preset, presetType, presetValue }) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setPresetType(presetType);
    setPresetValue(presetValue);

    track('dn_view_preset', {
      range_preset: preset,
      range_preset_type: presetType,
      range_preset_value: presetValue
    });
  };

  // Fonction pour afficher un résumé des filtres appliqués
  const getFilterSummary = () => {
    const parts = [];

    if (selectedProject) parts.push(selectedProject.label);

    if (startDate && endDate) {
      const start = startDate.toLocaleDateString('fr-FR');
      const end = endDate.toLocaleDateString('fr-FR');
      parts.push(`${start} - ${end}`);
    }

    if (selectedShopSigns.length > 0) {
      parts.push(`${selectedShopSigns.length}&nbsp;enseigne${selectedShopSigns.length > 1 ? 's' : ''}`);
    }

    if (selectedCategories.length > 0) {
      parts.push(`${selectedCategories.length}&nbsp;catégorie${selectedCategories.length > 1 ? 's' : ''}`);
    }

    return parts.join(' • ');
  };

  return (
    <div className="mb-2">
      {/* Toggle header pour mobile/tablet - masqué en desktop */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer md:hidden relative"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Titre centré - affiché uniquement quand le composant est déplié */}
        {isExpanded && (
          <div className="absolute inset-x-0 text-center">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtres</div>
          </div>
        )}

        {/* Résumé des filtres - affiché uniquement quand replié */}
        <div className={`flex-1 ${isExpanded ? 'invisible' : 'visible'} max-w-[calc(100%-2rem)]`}>
          {!isExpanded && (
            <div
              className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 text-left"
              dangerouslySetInnerHTML={{ __html: getFilterSummary() }}
            />
          )}
        </div>

        {/* Chevron (à droite) */}
        <div className="ml-3 z-10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isExpanded ? 'transform rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Contenu du filtre - toujours visible en desktop */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block py-4 md:pt-4 pt-0 border-t md:border-t-0 border-gray-100 dark:border-gray-700`}>
        <div className={`grid grid-cols-1 md:grid-cols-${jail ? '3' : '4'} gap-3 mt-4 md:mt-0`}>
          {/* Project Selection */}
          {!jail && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {_t('Project')}
              </label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={projects.sort((a, b) => a.label.localeCompare(b.label)).map(project => ({
                  ...project,
                  label: obfuscate && project.label && project.label.length > 2 ? `${project.label.slice(0, 2)}${'*'.repeat(Math.max(0, project.label.length - 2))}` : project.label
                }))}
                value={selectedProject ? {
                  ...selectedProject,
                  label: obfuscate && selectedProject.label && selectedProject.label.length > 2 ? `${selectedProject.label.slice(0, 2)}${'*'.repeat(Math.max(0, selectedProject.label.length - 2))}` : selectedProject.label
                } : selectedProject}
                onChange={setSelectedProject}
                isLoading={loading}
                placeholder={_t('Select a project...')}
                isDisabled={loading}
                menuPortalTarget={document.body}
                isClearable={true}

              />
            </div>
          )}

          {/* Date Range Selection */}
          <div>
            <div className="flex items-center justify-center mb-1.5">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center">
                {_t('Period')}
                <InfoTooltip text={_t('The selected period is compared to the equivalent previous period')} />
              </label>
            </div>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onDatesChange={handleDateRangeChange}
              maxDateRange={{ value: 12, unit: 'months' }}
              relative={false}
            />
          </div>

          {/* Shop Sign Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {_t('Shop Signs')} {selectedProject && `(${shopSigns.length})`}
            </label>
            <Select
              isMulti
              name="shopSigns"
              options={shopSigns}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder={_t('Filter by shop sign...')}
              noOptionsMessage={() => _t('No shop sign found')}
              value={selectedShopSigns}
              onChange={setSelectedShopSigns}
              isClearable={true}
              menuPortalTarget={document.body}
              isLoading={loading && selectedProject}
              isDisabled={!selectedProject}
              enableSelectAll
            />
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {_t('Product Families')} {selectedProject && `(${categories.length})`}
            </label>
            <Select
              isMulti
              name="categories"
              options={categories}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder={_t('Filter by category...')}
              noOptionsMessage={() => _t('No category found')}
              value={selectedCategories}
              onChange={setSelectedCategories}
              isClearable={true}
              menuPortalTarget={document.body}
              isLoading={loading && selectedProject}
              isDisabled={!selectedProject}
              enableSelectAll
            />
          </div>
        </div>

        {/* Date du dernier relevé */}
        <div className="flex justify-end mt-2">
          {loading ? (
            <div className="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ) : (
            lastMeasure && (
              <span className="text-xs text-gray-500 italic">
                {_t('Last measure')} : {new Date(lastMeasure).toLocaleDateString('fr-FR')}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default DNSelectionHeader;